<template lang="html">
  <section :id="id">
    <base-modal
      ref="baseModal"
      :maxHeight="modalHeight"
      @on-base-modal-close="cancelModal"
      v-if="isReady"
    >
      <!-- header  -->
      <template v-slot:header>
        <div class="m-title">
          {{ $t("metricHighlight") }}
        </div>
      </template>

      <element-description
        v-if="isReady"
        :label="descriptionText"
        :outer-container-class="'m-items'"
        :elem-description="desc"
        :value-container-class="'mt8 obj-select'"
        :focus="true"
        @on-element-description-changed="onDeDescriptionChanged"
      ></element-description>

      <div class="first-value">
        <div class="m-items">
          <div class="label-wrapper">
            <div class="label">{{ getMessageText(messages.ICON_INPUT) }}:</div>
          </div>
          <search-box-icons
            :value="firstIconValue"
            @on-search-box-icon-selected="onFirstIconSelected"
          />
        </div>

        <div class="m-items">
          <div class="label-wrapper">
            <div class="label">
              {{ getMessageText(messages.FIELD_LIST_ITEM_LABEL) }}:
            </div>
          </div>
          <div class="expression">
            <expressions
              class="exp-input"
              id="firstValueLabel"
              :data-elements="allSources"
              :selected-source="appContent"
              :initial-expression="firstValueLabelExp"
              :is-global="isGlobal"
              :content-type="appContent.type"
              :data-type="'String'"
              @on-expression-field-valid="onConfigExpressionValid"
            />
          </div>
        </div>

        <div class="m-items">
          <div class="label-wrapper">
            <div class="label">
              {{ getMessageText(messages.FIELD_LIST_ITEM_VALUE) }}:
            </div>
          </div>
          <div class="expression">
            <expressions
              class="exp-input"
              id="firstValueInput"
              :data-elements="allSources"
              :selected-source="appContent"
              :initial-expression="firstValueExp"
              :is-global="isGlobal"
              :content-type="appContent.type"
              @on-expression-field-valid="onConfigExpressionValid"
            />
          </div>
        </div>

        <div class="m-items" v-if="isFirstValueNumber">
          <div class="label-wrapper">
            <div class="label">
              {{ getMessageText(messages.EXP_LABEL_DECIMALS) }}:
            </div>
          </div>
          <div class="expression">
            <b-form-input
              class="decimal-input"
              :value="firstValueDecimals"
              type="number"
              min="0"
              @change="onFirstDecimalChanged"
            ></b-form-input>
          </div>
        </div>

        <div class="m-items" v-if="isFirstValueDate">
          <div class="label-wrapper"></div>

          <div class="checkbox-wrapper stack-checkbox">
            <label
              class="no-margin cursor-pointer"
              @click="toggleFirstLocalAware"
              >{{ $t("useLocaleFormatting") }}</label
            >
            <b-form-checkbox
              v-model="firstValueIsLocaleAwareFormatting"
            ></b-form-checkbox>
          </div>
        </div>
        <div
          class="m-items"
          v-if="isFirstValueDate && !firstValueIsLocaleAwareFormatting"
        >
          <div class="label-wrapper">
            <div class="label">
              {{ getMessageText(messages.EXP_LABEL_FORMAT) }}:
            </div>
          </div>
          <div class="expression">
            <expressions
              class="exp-input"
              id="firstValueDateFormat"
              :data-elements="allSources"
              :selected-source="appContent"
              :initial-expression="firstDateFormatExp"
              :is-global="isGlobal"
              :content-type="appContent.type"
              :data-type="'String'"
              @on-expression-field-valid="onConfigExpressionValid"
            />
          </div>
        </div>

        <div class="m-items">
          <div class="label-wrapper">
            <div class="label"></div>
          </div>
          <div class="expression">
            <user-action
              class="section-content"
              :all-sources="allSources"
              :is-global="true"
              :data-type="'string'"
              :action="firstActionExp"
              :app-id="appId"
              @on-user-action-change="onFirstActionChange"
            />
          </div>
        </div>
      </div>

      <div class="action-link">
        <chevrons
          :is-open="isSecondOpened"
          @on-chevon-click="toggleSecondSection"
        />
        <div @click="toggleSecondSection" class="actions-label">
          {{ $t("secondaryMetric") }}
        </div>
      </div>

      <div class="second-value" v-if="isSecondOpened">
        <div class="m-items">
          <div class="label-wrapper">
            <div class="label">{{ getMessageText(messages.ICON_INPUT) }}:</div>
          </div>
          <search-box-icons
            :value="secondIconValue"
            @on-search-box-icon-selected="onSecondIconSelected"
          />
        </div>

        <div class="m-items">
          <div class="label-wrapper">
            <div class="label">
              {{ getMessageText(messages.FIELD_LIST_ITEM_LABEL) }}:
            </div>
          </div>
          <div class="expression">
            <expressions
              class="exp-input"
              id="secondValueLabel"
              :data-elements="allSources"
              :selected-source="appContent"
              :initial-expression="secondValueLabelExp"
              :is-global="isGlobal"
              :content-type="appContent.type"
              :data-type="'String'"
              @on-expression-field-valid="onConfigExpressionValid"
            />
          </div>
        </div>

        <div class="m-items">
          <div class="label-wrapper">
            <div class="label">
              {{ getMessageText(messages.FIELD_LIST_ITEM_VALUE) }}:
            </div>
          </div>
          <div class="expression">
            <expressions
              class="exp-input"
              id="secondValueInput"
              :data-elements="allSources"
              :selected-source="appContent"
              :initial-expression="secondValueExp"
              :is-global="isGlobal"
              :content-type="appContent.type"
              @on-expression-field-valid="onConfigExpressionValid"
            />
          </div>
        </div>

        <div class="m-items" v-if="isSecondValueNumber">
          <div class="label-wrapper">
            <div class="label">
              {{ getMessageText(messages.EXP_LABEL_DECIMALS) }}:
            </div>
          </div>
          <div class="expression">
            <b-form-input
              class="decimal-input"
              :value="secondValueDecimals"
              type="number"
              min="0"
              @change="onSecondDecimalChanged"
            ></b-form-input>
          </div>
        </div>

        <div class="m-items" v-if="isSecondValueDate">
          <div class="label-wrapper"></div>
          <div class="checkbox-wrapper stack-checkbox">
            <label
              class="no-margin cursor-pointer"
              @click="toggleSecondLocalAware"
              >{{ $t("useLocaleFormatting") }}</label
            >
            <b-form-checkbox
              id="secondLabelCheckbox"
              v-model="secondValueIsLocaleAwareFormatting"
            ></b-form-checkbox>
          </div>
        </div>

        <div
          class="m-items"
          v-if="isSecondValueDate && !secondValueIsLocaleAwareFormatting"
        >
          <div class="label-wrapper">
            <div class="label">
              {{ getMessageText(messages.EXP_LABEL_FORMAT) }}:
            </div>
          </div>
          <div class="expression">
            <expressions
              class="exp-input"
              id="secondValueDateFormat"
              :data-elements="allSources"
              :selected-source="appContent"
              :initial-expression="secondDateFormatExp"
              :is-global="isGlobal"
              :content-type="appContent.type"
              :data-type="'String'"
              @on-expression-field-valid="onConfigExpressionValid"
            />
          </div>
        </div>

        <div class="m-items">
          <div class="label-wrapper">
            <div class="label"></div>
          </div>
          <div class="expression">
            <user-action
              class="section-content"
              :all-sources="allSources"
              :is-global="true"
              :data-type="'string'"
              :action="secondActionExp"
              :app-id="appId"
              @on-user-action-change="onSecondActionChange"
            />
          </div>
        </div>
      </div>

      <!-- footer  -->
      <template v-slot:footer>
        <div class="m-group-btns">
          <button class="m-button neutral" v-on:click="cancelModal">
            {{ getMessageText(messages.CANCEL) }}
          </button>
          <button
            class="m-button brand"
            @click="saveConfig"
            :disabled="isSubmitDisabled"
          >
            {{ getMessageText(messages.OK) }}
          </button>
        </div>
      </template>
    </base-modal>
  </section>
</template>

<script lang="js">
  import store from "@/store";
  import Chevrons from "@/views/drag-drop-pages/components/Chevrons";
  import {STUDIO_DATA_TYPE} from "@/static/global-variables";
  import expressionBuilder from "@/views/drag-drop-pages/components/utils/expression-builder";
  import dragAndDrop from "@/views/drag-drop-pages/components/utils/dragAndDrop";
  import BaseModal from "@/views/drag-drop-pages/components/modal/BaseModal";
  import UserAction from "@/views/drag-drop-pages/components/builder/modal/UserAction";
  import Expressions from "@/components/Expressions";
  import SearchBoxIcons from "@/views/drag-drop-pages/components/searchbox/searchicons/SearchBoxIcons";
  import ElementDescription from "@/components/ElementDescription";
  import utils from "@/utils";

  export default  {
    name: "add-metric-modal",
    components: {
      Chevrons,
      BaseModal,
      UserAction,
      Expressions,
      SearchBoxIcons,
      ElementDescription
    },
    props: {
      id: {
        type: String
      },
      editData: {
        type: Object
      },
      comparables: {
        type: Object
      }
    },
    mounted () {
      if(this.editData) {
        this.setupEditModalData(this.editData);
      }
      this.isReady = true;
    },
    computed: {
      messages() {
        return store.state.messages;
      },
      isFirstValueNumber() {
        return this.firstValueExp && this.isDataTypeNumber(this.firstValueExp);
      },
      isFirstValueDate() {
        return this.firstValueExp && this.isDataTypeDate(this.firstValueExp);
      },
      isFirstValueValid() {
        if (!this.firstValueExp) {
          return false;
        }

        if (this.isDataTypeNumber(this.firstValueExp)) {
          return (Number.isInteger(this.firstValueDecimals));
        } else if (this.isDataTypeDate(this.firstValueExp) && !this.firstValueIsLocaleAwareFormatting) {
          return !!this.firstDateFormatExp;
        }

        return true;
      },
      isFirstLabelValid() {
        return !!this.firstValueLabelExp
      },
      isSecondValueNumber() {
        return this.secondValueExp && this.isDataTypeNumber(this.secondValueExp);
      },
      isSecondValueDate() {
        return this.secondValueExp && this.isDataTypeDate(this.secondValueExp);
      },
      isSecondPairValid() {
        if (!this.secondValueLabelExp && !this.secondValueExp) {
          return true;
        } else if ((this.secondValueLabelExp && !this.secondValueExp) || (!this.secondValueLabelExp && this.secondValueExp)) {
          return false;
        } else {
          if (this.isDataTypeNumber(this.secondValueExp)) {
            return (Number.isInteger(this.secondValueDecimals) && this.secondValueDecimals >= 0);
          } else if (this.isDataTypeDate(this.secondValueExp) && !this.secondValueIsLocaleAwareFormatting) {
            return !!this.secondDateFormatExp;
          }

          return true;
        }
      },

      isSubmitDisabled() {
        // app is not deployable
        if (!!this.appContent && !this.appContent.isDeployable) {
          return true;
        }

        if (!this.isFirstValueValid || !this.isFirstLabelValid || !utils.isValidAction(this.firstActionExp)) {
          return true;
        }

        let isSecondInvalid = false;
        if ((!this.secondValueLabelExp || !this.secondValueExp) && this.secondActionExp) {
          isSecondInvalid = true;
        } else {
          isSecondInvalid = !this.isSecondPairValid || !utils.isValidAction(this.secondActionExp);
        }

        return isSecondInvalid;
      },
      appId() {
        return dragAndDrop.getAppId();
      },
      appContent() {
        return JSON.parse(JSON.stringify(expressionBuilder.getAppContent(this.appId)));
      },
      allSources() {
        let output = null;
        if (this.appContent) {
          output = this.appContent.dataElements || {};
        } else {
          console.error(`No app content found for appId ${this.appId}`);
        }
        return output;
      },
      desc() {
        return this.editData ? this.editData.props.description : "";
      },
      descriptionText() {
        return this.getMessageText(this.messages.DESCRIPTION);
      }
    },
    data () {
      return {
        modalHeight: 550,
        isGlobal: true,
        firstIconValue: null,
        firstValueExp: null,
        firstValueDecimals: null,
        firstDateFormatExp: null,
        firstValueLabelExp: null,
        firstActionExp: null,
        isSecondOpened: false,
        firstValueIsLocaleAwareFormatting: true,

        secondIconValue: null,
        secondValueExp: null,
        secondValueDecimals: null,
        secondDateFormatExp: null,
        secondValueLabelExp: null,
        secondActionExp: null,
        secondValueIsLocaleAwareFormatting: true,
        isReady: false
      }
    },
    methods: {
      onFirstIconSelected(ctx) {
        this.firstIconValue = ctx ? ctx.icon : null;
      },
      onSecondIconSelected(ctx) {
        this.secondIconValue = ctx ? ctx.icon : null;
      },
      toggleSecondSection() {
        this.isSecondOpened = !this.isSecondOpened;
      },
      isDataTypeNumber(expression) {
        return expression && expression.dataType.toLowerCase() === STUDIO_DATA_TYPE.NUMBER;
      },

      isDataTypeDate(expression) {
        return expression && expression.dataType.toLowerCase() === STUDIO_DATA_TYPE.DATE;
      },

      onFirstDecimalChanged(val) {
        this.firstValueDecimals = Number(val);
      },

      onSecondDecimalChanged(val) {
        this.secondValueDecimals = Number(val);
      },

      onConfigExpressionValid(ctx) {
        const result = ctx.status ? ctx.confExpression : null;

        if(ctx.id === "firstValueInput") {
          this.firstValueExp = result;
        } else if (ctx.id === "firstValueDateFormat") {
          this.firstDateFormatExp = result;
        } else if (ctx.id === "firstValueLabel") {
          this.firstValueLabelExp = result;
        } else if(ctx.id === "secondValueInput") {
          this.secondValueExp = result;
        } else if (ctx.id === "secondValueDateFormat") {
          this.secondDateFormatExp = result;
        } else if (ctx.id === "secondValueLabel") {
          this.secondValueLabelExp = result;
        }
      },
      toggleFirstLocalAware() {
        this.firstValueIsLocaleAwareFormatting = !this.firstValueIsLocaleAwareFormatting;
      },
      toggleSecondLocalAware() {
        this.secondValueIsLocaleAwareFormatting = !this.secondValueIsLocaleAwareFormatting;
      },
      setupEditModalData(editData) {
        if(editData) {
          this.onDeDescriptionChanged(editData.props.description);
          this.firstIconValue = editData.props.first.icon;
          this.firstValueExp = editData.props.first.value;
          this.firstValueLabelExp = editData.props.first.label;
          this.firstActionExp = editData.props.first.valueAction;
          if (this.isDataTypeNumber(this.firstValueExp)) {
            this.firstValueDecimals = editData.props.first.decimals;
          }
          if (this.isDataTypeDate(this.firstValueExp) && editData.props.first.formatExpression) {
            this.firstDateFormatExp = editData.props.first.formatExpression;
            this.firstValueIsLocaleAwareFormatting = false;
          }

          if (editData.props.second) {
            this.secondIconValue = editData.props.second.icon;
            this.secondValueExp = editData.props.second.value;
            this.secondValueLabelExp = editData.props.second.label;
            this.secondActionExp = editData.props.second.valueAction;
            if (this.isDataTypeNumber(this.secondValueExp)) {
              this.secondValueDecimals = editData.props.second.decimals;
            }
            if (this.isDataTypeDate(this.secondValueExp) && editData.props.second.formatExpression) {
              this.secondDateFormatExp = editData.props.second.formatExpression;
              this.secondValueIsLocaleAwareFormatting = false;
            }
            this.isSecondOpened = true;
          }
        }
      },
      cancelModal() {
        const EVENT = "on-add-modal-close";
        if(this.$listeners[EVENT] || this._events[EVENT]) {
          dragAndDrop.makeAllComponentsDraggable();
          this.$emit(EVENT, {id: this.id});
        }
      },
      formFirstValueExpObject() {
        let output = {
          value: this.firstValueExp,
          label: this.firstValueLabelExp,
          valueAction: this.firstActionExp,
          icon: this.firstIconValue
        }
        if (this.isDataTypeNumber(this.firstValueExp)) {
          output.decimals = this.firstValueDecimals;
        } else if (this.isDataTypeDate(this.firstValueExp)) {
          output.formatExpression = this.firstValueIsLocaleAwareFormatting? null : this.firstDateFormatExp;
        }

        return output;
      },

      formSecondValueExpObject() {
        let output = null;
        if (this.secondValueExp) {
          output = {
            value: this.secondValueExp,
            label: this.secondValueLabelExp,
            valueAction: this.secondActionExp,
            icon: this.secondIconValue
          }

          if (this.isDataTypeNumber(this.secondValueExp)) {
            output.decimals = this.secondValueDecimals;
          } else if (this.isDataTypeDate(this.secondValueExp)) {
            output.formatExpression = this.secondValueIsLocaleAwareFormatting? null : this.secondDateFormatExp;
          }
        }

        return output;
      },

      onFirstActionChange(ctx) {
        this.firstActionExp = ctx;
      },
      onSecondActionChange(ctx) {
        this.secondActionExp = ctx;
      },
      saveConfig() {
        const obj = {
          modal: {
            id: this.id,
            create: "MetricHighlight",
            editElementId: this.editData ? this.editData.id: null
          },
          props: {
            description: this.elementDescription,
            data: {
              first: this.formFirstValueExpObject(),
              second: this.formSecondValueExpObject()
            }
          }
        };
        const EVENT = "on-add-modal-done";
        if(this.$listeners[EVENT] || this._events[EVENT]) {
          dragAndDrop.makeAllComponentsDraggable();
          this.$emit(EVENT, obj);
        }
      },
      onDeDescriptionChanged(input) {
        this.elementDescription = input;
      }
    }
  }
</script>

<style scoped lang="scss">
  .m-items {
    grid-template-columns: 6rem auto;
    margin-bottom: 10px;
    color: var(--DEFAULT_TEXT_BLACK);
    .label {
      overflow-wrap: anywhere;
      padding-top: 0.2rem;
    }
    .label-wrapper {
      padding-right: 1rem;
    }
    .expression {
      .exp-input {
        width: 100%;
      }
      .section-content {
        flex-basis: 100%;
      }

      > .config-field {
        margin-top: 0;
      }
    }
    .expression,
    .label {
      display: flex;
      align-items: center;
      min-height: 2.5rem;
    }
  }
  .m-title {
    text-align: left;
  }
  .input-placeholder {
    width: 100%;
  }
  .action-link {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 0.75rem;
    //color: var(--BTN_BG_BLUE);
  }
  .actions-label {
    padding-left: 0.3rem;
    font-size: 0.8125rem;
  }
</style>
